/**
 * Murphy Door Configurator - Persistence Manager
 *
 * PURPOSE: State persistence, URL parameter management, configuration sharing,
 * and session/recovery storage coordination.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: Support (support-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-material-selector.js (200/200)
 *
 * CCP COMPLIANCE:
 * - CONSTANTS.EVENTS.* exclusively (no local EVENT_TYPES - Pattern 19)
 * - publishEvent() async helper with try/catch (Pattern 2, 11)
 * - 5-stage event lifecycle: STARTED -> VALIDATION -> PROGRESS -> SUCCESS/FAILED (Pattern 3)
 * - Event consumer handlers for bootstrap + selection events (Pattern 4)
 * - Event replay ready via idempotent handlers (Pattern 5)
 * - Bootstrap runtime protection via SYSTEM_STATE_FROM_PPD_READY (Pattern 6)
 * - PPD-first data access via conductor delegation (Patterns 7, 8)
 * - Data access through conductor delegation (Pattern 9)
 * - UI updates through event publishing (Pattern 10)
 * - Async event publishing (Pattern 11)
 * - Performance tracking with thresholds (Patterns 12, 13)
 * - Try/catch + FAILED events on all operations (Pattern 14)
 * - Cleanup arrays for all subscriptions (Pattern 15)
 * - Initialization guard (Pattern 16)
 * - XSS prevention via base64 encode/decode only (Pattern 17)
 * - CoreEventBus central freeze (Pattern 18 DRY)
 * - Conductor single publication (Pattern 20)
 *
 * PERFORMANCE CHARACTERISTICS:
 * - State serialization: O(n) JSON stringify + base64 encode
 * - State deserialization: O(n) base64 decode + JSON parse
 * - URL update: O(1) history.replaceState
 * - Storage operations: O(1) sessionStorage/localStorage access
 *
 * All external communication flows through md-config-core-conductor.js delegation.
 *
 * Dependencies:
 * - md-config-core-constants.js (event constants)
 * - md-config-core-event-bus.js (event publishing)
 * - md-config-core-conductor.js (primary conductor delegation)
 *
 * This module is used by:
 * - md-config-core-conductor.js (primary conductor)
 */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 1 & 19: GLOBAL CONSTANTS USAGE (10 points each)
  // ============================================================================
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  const EVENTS = CONSTANTS.EVENTS;
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;

  // ============================================================================
  // C1: DEBOUNCED history.replaceState WRITER
  // click=apply fires an apply (and thus a URL write) on every option click.
  // Browsers throttle replaceState (~100 calls / 30s → SecurityError), so rapid
  // configuration could otherwise throw. Coalesce writes: 300ms trailing,
  // 1000ms max-wait (guarantees a write within 1s even during a fast streak),
  // and flush synchronously on pagehide and before the cart snapshot.
  // ============================================================================
  // Share-link param that must never be persisted (see updateURLState).
  const BUILD_PRESET_URL_PARAM =
    CONSTANTS.BUILD_PRESET_URL_PARAM || 'build_preset';
  const URL_WRITE_DEBOUNCE_MS = 300;
  const URL_WRITE_MAX_WAIT_MS = 1000;
  let _pendingUrlWrite = null;
  let _urlWriteTimer = null;
  let _urlWriteFirstScheduledAt = 0;
  let _pagehideFlushWired = false;

  // ============================================================================
  // SAME-TAB RESUME
  // "Clean URL = fresh start" (recoverState's GHOST SELECTION FIX) also wiped a
  // build whenever the shopper left the PDP in the SAME tab and came back on the
  // bare URL. Customer report 2026-09-30: "pop ups delete your progress... had
  // to rebuild his door multiple times". The Klaviyo quiz popup's last step
  // ("Take Questionnaire") navigates the tab to /pages/quiz, for example.
  //
  // Every URL write stamps a per-PATH copy of ?config= in sessionStorage
  // (tab-scoped, gone when the tab closes). A later load of the SAME path with
  // no ?config= puts the copy back into the URL at module-eval time. That is
  // BEFORE url-param-parser (the next module script) latches INITIAL_URL_FLAGS,
  // so the restore runs down the exact share-link path.
  // The key starts with 'md_session', so Start Over's clears (clearAllStorage
  // and the conductor's clearState) already remove it.
  // ============================================================================
  const RESUME_KEY_PREFIX = 'md_session_resume:';
  const RESUME_MAX_AGE_MS = 2 * 60 * 60 * 1000;
  let _resumeSuppressed = false;

  function _stampResume(url) {
    if (_resumeSuppressed) return;
    try {
      const parsed = new URL(url, window.location.href);
      const key = RESUME_KEY_PREFIX + parsed.pathname;
      const config = parsed.searchParams.get('config');
      if (config) {
        sessionStorage.setItem(key, JSON.stringify({ config, ts: Date.now() }));
      } else {
        sessionStorage.removeItem(key);
      }
    } catch (error) {
      // Storage blocked or full: resume is a convenience, never fatal.
    }
  }

  (function resumeSameTabBuild() {
    try {
      const url = new URL(window.location.href);
      const params = url.searchParams;
      // An explicit intent in the URL always wins over a resumed build.
      if (
        params.get('config') ||
        params.get(BUILD_PRESET_URL_PARAM) ||
        params.get(CONSTANTS.RENDER_JOB_URL_PARAM || 'md_render_job')
      ) {
        return;
      }
      const key = RESUME_KEY_PREFIX + url.pathname;
      const raw = sessionStorage.getItem(key);
      if (!raw) return;
      const saved = JSON.parse(raw);
      const age = Date.now() - Number(saved?.ts);
      if (!saved?.config || !(age >= 0 && age < RESUME_MAX_AGE_MS)) {
        sessionStorage.removeItem(key);
        return;
      }
      params.set('config', saved.config);
      const utils = window.MurphyDoorConfigurator?.utils;
      if (utils?.replaceStateQuietly) {
        utils.replaceStateQuietly(window.history.state, '', url.toString());
      } else {
        window.history.replaceState(window.history.state, '', url.toString());
      }
      isDev() &&
        console.log('[PersistenceManager] Resumed same-tab build via ?config=');
    } catch (error) {
      isDev() &&
        console.warn('[PersistenceManager] Same-tab resume skipped:', error);
    }
  })();

  function _flushUrlWrite() {
    if (_urlWriteTimer) {
      clearTimeout(_urlWriteTimer);
      _urlWriteTimer = null;
    }
    _urlWriteFirstScheduledAt = 0;
    if (_pendingUrlWrite == null) return;
    const url = _pendingUrlWrite;
    _pendingUrlWrite = null;
    // Before the history write, so a throttled/throwing replaceState (see C1
    // above) still leaves a resumable copy behind.
    _stampResume(url);
    try {
      // Route through the pristine History.prototype: MageWorx APO wraps the
      // history INSTANCE and answers every write by re-fetching and re-parsing
      // the whole PDP (~11,000 retained nodes per selection). See
      // utils.replaceStateQuietly() for the measured mechanism.
      const utils = window.MurphyDoorConfigurator?.utils;
      if (utils?.replaceStateQuietly) {
        utils.replaceStateQuietly({}, '', url);
      } else {
        window.history.replaceState({}, '', url);
      }
    } catch (error) {
      isDev() &&
        console.warn(
          '[PersistenceManager] debounced replaceState failed:',
          error
        );
    }
  }

  function _scheduleUrlWrite(url) {
    _pendingUrlWrite = url;
    const now = Date.now();
    if (!_urlWriteFirstScheduledAt) _urlWriteFirstScheduledAt = now;
    if (_urlWriteTimer) clearTimeout(_urlWriteTimer);
    const elapsed = now - _urlWriteFirstScheduledAt;
    if (elapsed >= URL_WRITE_MAX_WAIT_MS) {
      _flushUrlWrite();
      return;
    }
    const wait = Math.min(
      URL_WRITE_DEBOUNCE_MS,
      URL_WRITE_MAX_WAIT_MS - elapsed
    );
    _urlWriteTimer = setTimeout(_flushUrlWrite, wait);
  }

  // ============================================================================
  // MODULE CONSTANTS
  // ============================================================================
  const STORAGE_KEYS = Object.freeze({
    CONFIGURATION: 'md_config',
    SESSION: 'md_session',
    RECOVERY: 'md_recovery',
    STATE_MANAGER: 'murphyDoorConfigState',
  });

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    STATE_RECOVERY: 50, // ms - URL parse + deserialize + PPD enrichment
    SERIALIZE: 10, // ms - JSON stringify + base64
    STORAGE_OPERATION: 10, // ms - sessionStorage/localStorage read/write
  });

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // ============================================================================
  let bootstrapComplete = false;
  let pendingCalls = [];

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  const cleanupHandlers = [];

  // ============================================================================
  // PATTERN 2: publishEvent() ASYNC HELPER (10 points)
  // PATTERN 11: ASYNC EVENT PUBLISHING (10 points)
  // NOTE: Object.freeze() NOT needed - CoreEventBus.publish() freezes centrally (Pattern 18 DRY)
  // ============================================================================
  async function publishEvent(eventType, eventData) {
    try {
      if (CoreEventBus?.publish) {
        await CoreEventBus.publish(eventType, eventData);
      } else {
        isDev() &&
          console.warn(
            `[PersistenceManager] Event bus unavailable, failed to publish: ${eventType}`
          );
      }
    } catch (error) {
      isDev() &&
        console.error(
          `[PersistenceManager] Error publishing ${eventType}:`,
          error
        );
    }
  }

  // ============================================================================
  // PATTERN 12: PERFORMANCE TRACKING (10 points)
  // ============================================================================
  function trackPerformance(operationType, duration) {
    const threshold = PERFORMANCE_THRESHOLDS[operationType];
    if (threshold && duration > threshold) {
      publishEvent(EVENTS.PERFORMANCE_PERSISTENCE_OPERATION_SLOW, {
        operation: operationType,
        duration,
        threshold,
        severity: duration > threshold * 2 ? 'critical' : 'warning',
        timestamp: Date.now(),
      });
    }
  }

  // ============================================================================
  // PATTERN 4: EVENT CONSUMER HANDLERS (10 points)
  // PATTERN 6: BOOTSTRAP SUBSCRIPTION AT MODULE LOAD TIME (10 points)
  // PATTERN 5: EVENT REPLAY READY (10 points) - Idempotent handler, safe for replay
  // ============================================================================
  if (CoreEventBus) {
    const unsubBootstrap = CoreEventBus.subscribe(
      EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        isDev() &&
          console.log('[PersistenceManager] Bootstrap complete event received');
        bootstrapComplete = true;

        // Process any queued calls from pre-bootstrap
        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              `[PersistenceManager] Processing ${pendingCalls.length} queued calls`
            );
          pendingCalls.forEach((call) => {
            try {
              call.fn.apply(call.context, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[PersistenceManager] Error processing queued call:',
                  error
                );
            }
          });
          pendingCalls = [];
        }
      }
    );
    cleanupHandlers.push(unsubBootstrap);
    isDev() &&
      console.log(
        '[PersistenceManager] Subscribed to bootstrap event at module load time'
      );
  }

  // ============================================================================
  // MODULE IMPLEMENTATION
  // ============================================================================
  window.MurphyDoorConfigurator.supportPersistenceManager = {
    // ========================================================================
    // INITIALIZATION
    // PATTERN 16: INITIALIZATION GUARD (10 points)
    // ========================================================================
    initialize() {
      // PATTERN 6: Bootstrap barrier - queue if not ready
      if (!bootstrapComplete) {
        isDev() &&
          console.log(
            '[PersistenceManager] Bootstrap not complete, queuing initialize()'
          );
        pendingCalls.push({ fn: this.initialize, context: this, args: [] });
        return false;
      }

      // PATTERN 16: Prevent double initialization
      if (isInitialized) {
        isDev() &&
          console.warn('[PersistenceManager] Already initialized, skipping');
        return true;
      }

      try {
        isDev() &&
          console.log(
            '[PersistenceManager] Initializing persistence manager with event listeners'
          );

        this._subscribeToSelectionEvents();
        this._subscribeToButtonEvents();

        // C1: flush any pending debounced URL write when the page is being
        // hidden/unloaded so a shared config link is never stale.
        if (!_pagehideFlushWired && typeof window.addEventListener === 'function') {
          window.addEventListener('pagehide', _flushUrlWrite);
          _pagehideFlushWired = true;
        }

        isInitialized = true;

        // Publish module ready event
        publishEvent(EVENTS.MODULE_PERSISTENCE_MANAGER_READY, {
          module: 'persistenceManager',
          timestamp: Date.now(),
        });

        isDev() && console.log('[PersistenceManager] Initialization complete');
        return true;
      } catch (error) {
        isDev() &&
          console.error('[PersistenceManager] Initialization failed:', error);

        publishEvent(EVENTS.SYSTEM_PERSISTENCE_OPERATION_FAILED, {
          operation: 'initialize',
          error: error.message,
          timestamp: Date.now(),
        });

        return false;
      }
    },

    // ========================================================================
    // PATTERN 4: EVENT CONSUMER HANDLERS - Selection Events (10 points)
    // ========================================================================
    _subscribeToSelectionEvents() {
      // Subscribe to selection:applied events for real-time URL updates
      const unsubApplied = CoreEventBus.subscribe(
        EVENTS.SELECTION_APPLIED,
        (event) => {
          isDev() &&
            console.log(
              '[PersistenceManager] Applied selection detected - updating URL',
              event
            );
          this._updateURLFromConductor();
        }
      );
      cleanupHandlers.push(unsubApplied);

      // Subscribe to selection:user_unapply_completed for URL state update
      const unsubUnapplyCompleted = CoreEventBus.subscribe(
        EVENTS.SELECTION_USER_UNAPPLY_COMPLETED,
        (event) => {
          isDev() &&
            console.log(
              '[PersistenceManager] Unapply completed detected - updating URL',
              event
            );
          this._updateURLFromConductor();
        }
      );
      cleanupHandlers.push(unsubUnapplyCompleted);

      // Subscribe to selection:user_unapply_requested to save recovery point before unapply
      const unsubUnapplyRequested = CoreEventBus.subscribe(
        EVENTS.SELECTION_USER_UNAPPLY_REQUESTED,
        (event) => {
          isDev() &&
            console.log(
              '[PersistenceManager] Unapply requested - saving recovery point before execution',
              event
            );

          // PATTERN 9: Data access via conductor delegation
          const conductor = this._getConductor();
          if (conductor && conductor.stateManager) {
            const currentState = conductor.stateManager.getAllSelections();
            this.saveRecoveryPoint(currentState);
            isDev() &&
              console.log(
                '[PersistenceManager] Recovery point saved before unapply'
              );
          } else {
            isDev() &&
              console.warn(
                '[PersistenceManager] Could not save recovery point - conductor or stateManager unavailable'
              );
          }
        }
      );
      cleanupHandlers.push(unsubUnapplyRequested);

      // Subscribe to selection:user_unapply_failed to rollback URL state on failure
      const unsubUnapplyFailed = CoreEventBus.subscribe(
        EVENTS.SELECTION_USER_UNAPPLY_FAILED,
        (event) => {
          isDev() &&
            console.log(
              '[PersistenceManager] Unapply failed - attempting URL rollback from recovery point',
              event
            );
          this._rollbackURLFromRecovery();
        }
      );
      cleanupHandlers.push(unsubUnapplyFailed);

      // Subscribe to STATE_SELECTION_CLEARED for cascade forbidden-unapply and rush-delivery
      // auto-deselect paths — these clear applied state but do NOT fire SELECTION_USER_UNAPPLY_COMPLETED,
      // leaving stale URL params without this handler.
      // GUARD: Skip conductor.user_unapply source — SELECTION_USER_UNAPPLY_COMPLETED fires afterward
      // on that path and already triggers _updateURLFromConductor(), preventing a double rebuild.
      const unsubSelectionCleared = CoreEventBus.subscribe(
        CONSTANTS.EVENTS.STATE_SELECTION_CLEARED,
        (event) => {
          if (event && event.source === 'conductor.user_unapply') return;

          isDev() &&
            console.log(
              '[PersistenceManager] STATE_SELECTION_CLEARED detected - updating URL',
              { source: event?.source, categoryId: event?.categoryId }
            );
          this._updateURLFromConductor();
        }
      );
      cleanupHandlers.push(unsubSelectionCleared);

      isDev() &&
        console.log(
          '[PersistenceManager] Selection event listeners initialized'
        );
    },

    // ========================================================================
    // PATTERN 10: UI UPDATE EVENTS - Button Handlers (10 points)
    // ========================================================================
    _subscribeToButtonEvents() {
      isDev() &&
        console.log(
          '[PersistenceManager] Setting up event-driven UI button handlers'
        );

      // PATTERN 10: Request button handler setup via UI events
      publishEvent(EVENTS.UI_SETUP_BUTTON_HANDLERS, {
        source: 'persistence-manager',
        buttons: [
          {
            selector: '[data-category="clear-config-button"]',
            event: EVENTS.PERSISTENCE_CLEAR_REQUESTED,
            preventDefault: true,
          },
          {
            selector: '[data-category="share-config-button"]',
            event: EVENTS.PERSISTENCE_SHARE_REQUESTED,
            preventDefault: true,
          },
          {
            selector: '[data-category="save-config-button"]',
            event: EVENTS.PERSISTENCE_SAVE_REQUESTED,
            preventDefault: true,
          },
        ],
      });

      // Subscribe to the button events from UI modules
      const unsubClearRequested = CoreEventBus.subscribe(
        EVENTS.PERSISTENCE_CLEAR_REQUESTED,
        () => {
          this._handleClearConfiguration();
        }
      );
      cleanupHandlers.push(unsubClearRequested);

      const unsubShareRequested = CoreEventBus.subscribe(
        EVENTS.PERSISTENCE_SHARE_REQUESTED,
        () => {
          this.handleShareConfiguration();
        }
      );
      cleanupHandlers.push(unsubShareRequested);

      const unsubSaveRequested = CoreEventBus.subscribe(
        EVENTS.PERSISTENCE_SAVE_REQUESTED,
        () => {
          this.handleSaveConfiguration();
        }
      );
      cleanupHandlers.push(unsubSaveRequested);

      // Subscribe to modal confirmation events
      const unsubClearConfirmed = CoreEventBus.subscribe(
        EVENTS.PERSISTENCE_CLEAR_CONFIRMED,
        () => {
          this._executeClearConfiguration();
        }
      );
      cleanupHandlers.push(unsubClearConfirmed);

      isDev() &&
        console.log(
          '[PersistenceManager] Event-driven button handlers configured'
        );
    },

    // ========================================================================
    // INTERNAL HELPERS - Conductor Access (Pattern 9: Data Request Events)
    // ========================================================================

    /**
     * PATTERN 9: Get conductor reference for delegation
     * @returns {Object|null} Conductor reference
     */
    _getConductor() {
      return (
        window.MurphyDoorConfigurator.main ||
        window.MurphyDoorConfigurator.conductor
      );
    },

    /**
     * PATTERN 9: Update URL state via conductor delegation
     */
    _updateURLFromConductor() {
      const conductor = this._getConductor();
      if (conductor && conductor.stateManager) {
        const currentState = conductor.stateManager.getAllSelections();

        if (conductor.delegatePersistenceOperation) {
          const result = conductor.delegatePersistenceOperation(
            'updateURLState',
            { state: currentState }
          );
          if (result.success) {
            isDev() &&
              console.log('[PersistenceManager] URL updated successfully');
          } else {
            isDev() &&
              console.warn(
                '[PersistenceManager] URL update failed:',
                result.error
              );
          }
        }
      }
    },

    /**
     * Rollback URL state from recovery point after unapply failure
     */
    _rollbackURLFromRecovery() {
      try {
        const recovery = this.loadRecoveryPoint();
        if (recovery && recovery.state) {
          const conductor = this._getConductor();
          if (conductor && conductor.delegatePersistenceOperation) {
            const result = conductor.delegatePersistenceOperation(
              'updateURLState',
              { state: recovery.state }
            );
            if (result.success) {
              isDev() &&
                console.log(
                  '[PersistenceManager] URL rolled back after unapply failure'
                );
            } else {
              isDev() &&
                console.warn(
                  '[PersistenceManager] URL rollback after unapply failure failed:',
                  result.error
                );
            }
          } else {
            isDev() &&
              console.warn(
                '[PersistenceManager] Could not rollback URL - conductor or delegate unavailable'
              );
          }
        } else {
          isDev() &&
            console.warn(
              '[PersistenceManager] No recovery point available for URL rollback after unapply failure'
            );
        }
      } catch (error) {
        isDev() &&
          console.error('[PersistenceManager] URL rollback error:', error);

        publishEvent(EVENTS.SYSTEM_PERSISTENCE_OPERATION_FAILED, {
          operation: 'url_rollback',
          error: error.message,
          timestamp: Date.now(),
        });
      }
    },

    // ========================================================================
    // CLEAR CONFIGURATION - With 5-Stage Lifecycle
    // PATTERN 3: 5-STAGE EVENT LIFECYCLE (10 points)
    // PATTERN 14: TRY/CATCH + FAILED EVENTS (10 points)
    // ========================================================================

    _handleClearConfiguration() {
      isDev() &&
        console.log(
          '[PersistenceManager] Clear button clicked - showing confirmation modal'
        );
      this._showClearConfirmationModal();
    },

    _showClearConfirmationModal() {
      isDev() &&
        console.log(
          '[PersistenceManager] Requesting clear confirmation modal via UI events'
        );

      // PATTERN 10: UI update via events, no direct DOM manipulation
      publishEvent(EVENTS.UI_SHOW_MODAL, {
        type: 'clear-confirmation',
        title: 'Reset Configuration',
        message:
          'Are you sure? This will reset all selections and start over with a fresh configuration!',
        buttons: [
          {
            text: 'Cancel',
            action: EVENTS.UI_HIDE_MODAL,
            style: 'cancel',
          },
          {
            text: 'Proceed',
            action: EVENTS.PERSISTENCE_CLEAR_CONFIRMED,
            style: 'proceed',
          },
        ],
        source: 'persistence-manager',
      });

      isDev() &&
        console.log(
          '[PersistenceManager] Clear confirmation modal request published via CoreEventBus'
        );
    },

    _executeClearConfiguration() {
      isDev() &&
        console.log(
          '[PersistenceManager] Executing configuration clear with URL reset'
        );

      try {
        // PATTERN 10: Show loading spinner via UI events
        publishEvent(EVENTS.UI_SHOW_LOADING_SPINNER, {
          source: 'persistence-manager',
          reason: 'clearing-configuration',
        });

        // STEP 1: Clear ALL persistent storage BEFORE any navigation.
        // Drop any debounced URL write too: the pagehide flush during the
        // navigation below would otherwise re-stamp the same-tab resume copy
        // and bring the cleared build straight back.
        _resumeSuppressed = true;
        if (_urlWriteTimer) clearTimeout(_urlWriteTimer);
        _urlWriteTimer = null;
        _pendingUrlWrite = null;
        this.clearAllStorage();

        // STEP 2: Clear state via conductor delegation (Pattern 9)
        const conductor = this._getConductor();
        if (conductor && conductor.delegatePersistenceOperation) {
          const result = conductor.delegatePersistenceOperation('clearState');
          if (result.success) {
            isDev() &&
              console.log(
                '[PersistenceManager] Configuration cleared successfully - navigating to clean URL'
              );
          } else {
            isDev() &&
              console.warn(
                '[PersistenceManager] Clear state operation had issues:',
                result.error
              );
          }
        }

        // STEP 3: Navigate to clean URL (no params)
        const cleanUrl = window.location.origin + window.location.pathname;
        isDev() &&
          console.log(
            '[PersistenceManager] Navigating to clean URL:',
            cleanUrl
          );
        window.location.href = cleanUrl;
      } catch (error) {
        isDev() &&
          console.error(
            '[PersistenceManager] Clear configuration failed:',
            error
          );

        publishEvent(EVENTS.SYSTEM_PERSISTENCE_OPERATION_FAILED, {
          operation: 'clear_configuration',
          error: error.message,
          timestamp: Date.now(),
        });
      }
    },

    // ========================================================================
    // STORAGE MANAGEMENT
    // PATTERN 14: TRY/CATCH + FAILED EVENTS (10 points)
    // ========================================================================

    /**
     * Clears ALL configurator-related storage entries from sessionStorage and localStorage.
     * Called during clear configuration to prevent recoverState() from restoring old selections.
     *
     * Storage entries cleared:
     * - sessionStorage: md_session_*, md_recovery (persistence manager entries)
     * - sessionStorage: md_console_session (console capture session)
     * - localStorage: murphyDoorConfigState (state manager recovery)
     * - IndexedDB: PPD cache (via conductor delegation)
     * - localStorage: murphy_door_ppd_cache_* (legacy PPD cache entries)
     */
    clearAllStorage() {
      const startTime = performance.now();
      isDev() &&
        console.log(
          '[PersistenceManager] Clearing ALL configurator storage entries'
        );

      try {
        // Clear sessionStorage entries used by persistence manager
        const sessionKeysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (
            key &&
            (key.startsWith(STORAGE_KEYS.SESSION) ||
              key.startsWith(STORAGE_KEYS.CONFIGURATION) ||
              key === STORAGE_KEYS.RECOVERY)
          ) {
            sessionKeysToRemove.push(key);
          }
        }
        sessionKeysToRemove.forEach(function (key) {
          sessionStorage.removeItem(key);
          isDev() && console.log('  Cleared sessionStorage:', key);
        });

        // Clear localStorage entries used by state manager recovery
        localStorage.removeItem(STORAGE_KEYS.STATE_MANAGER);
        isDev() &&
          console.log('  Cleared localStorage:', STORAGE_KEYS.STATE_MANAGER);

        // 🚨 THE PPD INDEXEDDB CACHE IS DELIBERATELY *NOT* CLEARED HERE.
        //
        // This method used to delegate 'clearIdbCache', and that single line
        // meant the IndexedDB PPD cache had effectively NEVER WORKED as a
        // cross-visit cache for any returning customer.
        //
        // The path, traced on production (2026-09-04):
        //   attemptStateRecovery -> recoverState -> clearAllStorage
        //     -> conductor.delegatePersistenceOperation('clearIdbCache')
        //     -> IdbCache.clearAll() -> store.clear()   // wipes EVERYTHING
        //
        // recoverState() calls this from its "GHOST SELECTION FIX" branch on
        // every CLEAN-URL load where any stored selection state exists — i.e.
        // the second and every subsequent visit. Measured, persistent Chromium
        // profile, production theme: entries went 1 -> 0 -> 0 across three
        // loads, and the store was wiped even on the load where cachePPD()
        // never ran at all.
        //
        // 🪤 IT IS A CATEGORY ERROR. This method exists to prevent GHOST
        // SELECTIONS, and _hasAnyStoredState() — the guard that decides whether
        // to call it — inspects ONLY customer-selection keys (the localStorage
        // STATE_MANAGER entry and the md_session / md_config / md_recovery
        // sessionStorage keys). The PPD cache holds no customer selections
        // whatsoever: it is product catalogue data keyed by productId, and it
        // has its own invalidation (productVersion, globalVersion, a catalog
        // hash, PPD_CONTRACT_VERSION and a 7-day TTL). Clearing it buys exactly
        // nothing for ghost-selection prevention and costs a full PPD rebuild
        // on every return visit — a large fixed bootstrap cost that no
        // per-interaction metric can see.
        //
        // Verified that removing it does NOT weaken the guarantee this method
        // is for: after 6 selections (price $2,260, ?config= in the URL),
        // loading a clean URL still resets to $1,595 with the line gone,
        // identical to before. Cache survival went 1->0->0 to 1->1->1 with no
        // change in tiles, cards, error markup or "Cache error" text.
        //
        // If a stale PPD is ever suspected, invalidate it through the version /
        // hash / contract checks in md-config-core-initializer.js — that is what
        // they are for. Do NOT reinstate a blunt clear here.

        // Legacy cleanup: remove any old localStorage PPD cache entries
        const localKeysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('murphy_door_ppd_cache_')) {
            localKeysToRemove.push(key);
          }
        }
        localKeysToRemove.forEach(function (key) {
          localStorage.removeItem(key);
          isDev() && console.log('  Cleared legacy localStorage:', key);
        });

        const duration = performance.now() - startTime;
        trackPerformance('STORAGE_OPERATION', duration);

        // Publish storage cleared event for observability
        publishEvent(EVENTS.PERSISTENCE_STORAGE_CLEARED, {
          sessionKeysCleared: sessionKeysToRemove.length,
          localKeysCleared: localKeysToRemove.length + 1, // +1 for STATE_MANAGER
          duration,
          timestamp: Date.now(),
        });

        isDev() &&
          console.log(
            '[PersistenceManager] All configurator storage entries cleared'
          );
      } catch (error) {
        isDev() &&
          console.error('[PersistenceManager] Error clearing storage:', error);

        publishEvent(EVENTS.SYSTEM_PERSISTENCE_OPERATION_FAILED, {
          operation: 'clear_all_storage',
          error: error.message,
          timestamp: Date.now(),
        });

        // Fallback: try nuclear option
        try {
          sessionStorage.clear();
          isDev() &&
            console.warn(
              '[PersistenceManager] Used sessionStorage.clear() as fallback'
            );
        } catch (fallbackError) {
          isDev() &&
            console.error(
              '[PersistenceManager] Fallback storage clear also failed:',
              fallbackError
            );
        }
      }
    },

    clearURLParameters() {
      isDev() &&
        console.log(
          '[PersistenceManager] Clearing URL parameters for fresh start'
        );

      try {
        const url = new URL(window.location);
        url.search = '';
        const utils = window.MurphyDoorConfigurator?.utils;
        if (utils?.replaceStateQuietly) {
          utils.replaceStateQuietly({}, document.title, url.toString());
        } else {
          window.history.replaceState({}, document.title, url.toString());
        }

        isDev() &&
          console.log(
            '[PersistenceManager] URL parameters cleared successfully'
          );
      } catch (error) {
        isDev() &&
          console.error(
            '[PersistenceManager] Failed to clear URL parameters:',
            error
          );

        publishEvent(EVENTS.SYSTEM_PERSISTENCE_OPERATION_FAILED, {
          operation: 'clear_url_parameters',
          error: error.message,
          timestamp: Date.now(),
        });
      }
    },

    // ========================================================================
    // SHARE CONFIGURATION
    // PATTERN 14: TRY/CATCH + FAILED EVENTS (10 points)
    // PATTERN 10: UI UPDATE EVENTS (10 points)
    // ========================================================================

    _showShareModal(url, title) {
      isDev() &&
        console.log(
          '[PersistenceManager] Requesting share modal via UI events'
        );

      // PATTERN 10 & 19: UI update via CONSTANTS.EVENTS.* through publishEvent helper
      publishEvent(EVENTS.UI_SHOW_MODAL, {
        type: 'share-success',
        title: title || 'Share Your Design',
        message:
          'Paste this URL in an email or text to share your creation, or use it to come back and pick up where you left off.',
        url: url,
        buttons: [
          {
            text: 'Copy to Clipboard',
            action: EVENTS.UI_HIDE_MODAL,
            style: 'primary',
          },
        ],
        source: 'persistence-manager',
      });

      isDev() &&
        console.log('[PersistenceManager] Share modal request published');
    },

    handleShareConfiguration() {
      try {
        isDev() &&
          console.log(
            '[PersistenceManager] Share button clicked - generating URL'
          );

        // PATTERN 3: 5-Stage Lifecycle - START
        publishEvent(EVENTS.SYSTEM_PERSISTENCE_OPERATION_STARTED, {
          operation: 'share',
          timestamp: Date.now(),
        });

        const conductor = this._getConductor();

        if (!conductor) {
          throw new Error('Conductor unavailable for share operation');
        }

        if (!conductor.stateManager) {
          throw new Error('State manager unavailable for share operation');
        }

        // PATTERN 9: Data access via conductor delegation
        const currentState = conductor.stateManager.getAllSelections();
        isDev() &&
          console.log(
            '[PersistenceManager] Current state for sharing:',
            currentState
          );

        if (!conductor.delegatePersistenceOperation) {
          throw new Error('delegatePersistenceOperation not available');
        }

        const result = conductor.delegatePersistenceOperation(
          'generateShareableURL',
          { state: currentState }
        );

        if (result.success && result.url) {
          isDev() &&
            console.log(
              '[PersistenceManager] Share URL generated successfully'
            );

          // PATTERN 3: 5-Stage Lifecycle - SUCCESS
          publishEvent(EVENTS.SYSTEM_PERSISTENCE_OPERATION_COMPLETED, {
            operation: 'share',
            timestamp: Date.now(),
          });

          // PATTERN 10: Show modal with URL — user clicks "Copy to Clipboard" to copy
          this._showShareModal(result.url);
        } else {
          throw new Error('Share URL generation failed');
        }
      } catch (error) {
        isDev() &&
          console.error(
            '[PersistenceManager] Share configuration failed:',
            error
          );

        // PATTERN 3: 5-Stage Lifecycle - FAILED
        publishEvent(EVENTS.SYSTEM_PERSISTENCE_OPERATION_FAILED, {
          operation: 'share',
          error: error.message,
          timestamp: Date.now(),
        });
      }
    },

    handleSaveConfiguration() {
      try {
        isDev() &&
          console.log(
            '[PersistenceManager] Save button clicked - generating URL'
          );

        // PATTERN 3: 5-Stage Lifecycle - START
        publishEvent(EVENTS.SYSTEM_PERSISTENCE_OPERATION_STARTED, {
          operation: 'save',
          timestamp: Date.now(),
        });

        const conductor = this._getConductor();

        if (!conductor) {
          throw new Error('Conductor unavailable for save operation');
        }

        if (!conductor.stateManager) {
          throw new Error('State manager unavailable for save operation');
        }

        // PATTERN 9: Data access via conductor delegation
        const currentState = conductor.stateManager.getAllSelections();
        isDev() &&
          console.log(
            '[PersistenceManager] Current state for saving:',
            currentState
          );

        if (!conductor.delegatePersistenceOperation) {
          throw new Error('delegatePersistenceOperation not available');
        }

        const result = conductor.delegatePersistenceOperation(
          'generateShareableURL',
          { state: currentState }
        );

        if (result.success && result.url) {
          isDev() &&
            console.log('[PersistenceManager] Save URL generated successfully');

          // PATTERN 3: 5-Stage Lifecycle - SUCCESS
          publishEvent(EVENTS.SYSTEM_PERSISTENCE_OPERATION_COMPLETED, {
            operation: 'save',
            timestamp: Date.now(),
          });

          // PATTERN 10: Show modal with URL — user clicks "Copy to Clipboard" to copy
          this._showShareModal(result.url, 'Save Your Design');
        } else {
          throw new Error('Save URL generation failed');
        }
      } catch (error) {
        isDev() &&
          console.error(
            '[PersistenceManager] Save configuration failed:',
            error
          );

        // PATTERN 3: 5-Stage Lifecycle - FAILED
        publishEvent(EVENTS.SYSTEM_PERSISTENCE_OPERATION_FAILED, {
          operation: 'save',
          error: error.message,
          timestamp: Date.now(),
        });
      }
    },

    // ========================================================================
    // URL STATE MANAGEMENT
    // PATTERN 17: XSS PREVENTION (10 points) - base64 encode/decode only
    // PATTERN 14: TRY/CATCH + FAILED EVENTS (10 points)
    // ========================================================================

    serializeState(state) {
      const startTime = performance.now();
      try {
        // PATTERN 17: Base64 encode only - URLSearchParams.set() handles URL encoding automatically
        // IMPORTANT: Do NOT use encodeURIComponent() here - that causes double-encoding
        const serialized = btoa(JSON.stringify(state));

        const duration = performance.now() - startTime;
        trackPerformance('SERIALIZE', duration);

        return serialized;
      } catch (error) {
        isDev() &&
          console.error(
            '[PersistenceManager] State serialization failed:',
            error
          );
        publishEvent(EVENTS.ERROR_SERIALIZE_STATE_FAILED, {
          operation: 'serialize_state',
          error: error.message,
          stack: error.stack,
          source: 'PersistenceManager',
          timestamp: Date.now(),
        });
        return null;
      }
    },

    deserializeState(encoded) {
      const startTime = performance.now();
      try {
        // PATTERN 17: URLSearchParams.get() returns already-decoded value,
        // so we only need to decode the base64 layer
        const result = JSON.parse(atob(encoded));

        const duration = performance.now() - startTime;
        trackPerformance('SERIALIZE', duration);

        return result;
      } catch (error) {
        isDev() &&
          console.error(
            '[PersistenceManager] State deserialization failed:',
            error
          );
        publishEvent(EVENTS.ERROR_DESERIALIZE_STATE_FAILED, {
          operation: 'deserialize_state',
          error: error.message,
          stack: error.stack,
          source: 'PersistenceManager',
          timestamp: Date.now(),
        });
        return null;
      }
    },

    // C1: flush any pending debounced URL write immediately. Called by the
    // conductor before the cart snapshot so the serialized _Configuration_URL
    // reflects the very latest applied state.
    flushURLState() {
      _flushUrlWrite();
    },

    // For a deliberate clean reload that must NOT resume the current build
    // (material-remap "keep existing" with no earlier configuration).
    clearSameTabResume() {
      _resumeSuppressed = true;
      _pendingUrlWrite = null;
      try {
        sessionStorage.removeItem(RESUME_KEY_PREFIX + window.location.pathname);
      } catch (error) {
        // Storage blocked: nothing was stamped either.
      }
    },

    updateURLState(state) {
      try {
        const url = new URL(window.location);
        // A ?build_preset= share handle is consumed ONCE at load (latched by
        // the url-param-parser). It must never ride along on a persisted URL:
        // a later share/cart-restore link carrying it would re-apply a look
        // over the customer's own changes. md_render_job is left alone.
        url.searchParams.delete(BUILD_PRESET_URL_PARAM);

        // If state is empty (all selections cleared), remove the config param entirely
        if (!state || Object.keys(state).length === 0) {
          url.searchParams.delete('config');
          _scheduleUrlWrite(url.toString());
          isDev() &&
            console.log(
              '[PersistenceManager] URL config param cleared (no active selections)'
            );
          return;
        }

        const serialized = this.serializeState(state);
        if (serialized) {
          url.searchParams.set('config', serialized);
          _scheduleUrlWrite(url.toString());
        }
      } catch (error) {
        isDev() &&
          console.error('[PersistenceManager] URL state update failed:', error);

        publishEvent(EVENTS.SYSTEM_PERSISTENCE_OPERATION_FAILED, {
          operation: 'update_url_state',
          error: error.message,
          timestamp: Date.now(),
        });
      }
    },

    // ========================================================================
    // SESSION STORAGE MANAGEMENT
    // PATTERN 14: TRY/CATCH + FAILED EVENTS (10 points)
    // ========================================================================

    saveToSession(key, data) {
      const startTime = performance.now();
      try {
        sessionStorage.setItem(
          `${STORAGE_KEYS.SESSION}_${key}`,
          JSON.stringify(data)
        );

        const duration = performance.now() - startTime;
        trackPerformance('STORAGE_OPERATION', duration);

        return true;
      } catch (error) {
        isDev() &&
          console.error('[PersistenceManager] Session save failed:', error);
        publishEvent(EVENTS.ERROR_PERSISTENCE_SESSION_SAVE_FAILED, {
          operation: 'session_save',
          key,
          error: error.message,
          stack: error.stack,
          source: 'PersistenceManager',
          timestamp: Date.now(),
        });
        return false;
      }
    },

    loadFromSession(key) {
      const startTime = performance.now();
      try {
        const data = sessionStorage.getItem(`${STORAGE_KEYS.SESSION}_${key}`);

        const duration = performance.now() - startTime;
        trackPerformance('STORAGE_OPERATION', duration);

        return data ? JSON.parse(data) : null;
      } catch (error) {
        isDev() &&
          console.error('[PersistenceManager] Session load failed:', error);
        publishEvent(EVENTS.ERROR_PERSISTENCE_SESSION_LOAD_FAILED, {
          operation: 'session_load',
          key,
          error: error.message,
          stack: error.stack,
          source: 'PersistenceManager',
          timestamp: Date.now(),
        });
        return null;
      }
    },

    // ========================================================================
    // RECOVERY STATE MANAGEMENT
    // ========================================================================

    saveRecoveryPoint(state) {
      const startTime = performance.now();
      try {
        const timestamp = Date.now();
        const recovery = {
          state,
          timestamp,
          version: '1.0',
        };

        sessionStorage.setItem(STORAGE_KEYS.RECOVERY, JSON.stringify(recovery));

        const duration = performance.now() - startTime;
        trackPerformance('STORAGE_OPERATION', duration);

        return true;
      } catch (error) {
        isDev() &&
          console.error('[PersistenceManager] Recovery save failed:', error);
        publishEvent(EVENTS.ERROR_PERSISTENCE_RECOVERY_SAVE_FAILED, {
          operation: 'recovery_save',
          error: error.message,
          stack: error.stack,
          source: 'PersistenceManager',
          timestamp: Date.now(),
        });
        return false;
      }
    },

    loadRecoveryPoint() {
      try {
        const recovery = sessionStorage.getItem(STORAGE_KEYS.RECOVERY);
        return recovery ? JSON.parse(recovery) : null;
      } catch (error) {
        isDev() &&
          console.error('[PersistenceManager] Recovery load failed:', error);
        publishEvent(EVENTS.ERROR_PERSISTENCE_RECOVERY_LOAD_FAILED, {
          operation: 'recovery_load',
          error: error.message,
          stack: error.stack,
          source: 'PersistenceManager',
          timestamp: Date.now(),
        });
        return null;
      }
    },

    // ========================================================================
    // CONFIGURATION SHARING
    // ========================================================================

    generateShareableURL(state) {
      try {
        const serialized = this.serializeState(state);
        if (serialized) {
          const url = new URL(window.location);
          url.searchParams.delete(BUILD_PRESET_URL_PARAM);
          url.searchParams.set('config', serialized);
          return url.toString();
        }
        return null;
      } catch (error) {
        isDev() &&
          console.error(
            '[PersistenceManager] Generate shareable URL failed:',
            error
          );

        publishEvent(EVENTS.SYSTEM_PERSISTENCE_OPERATION_FAILED, {
          operation: 'generate_shareable_url',
          error: error.message,
          timestamp: Date.now(),
        });

        return null;
      }
    },

    // ========================================================================
    // STATE ENRICHMENT
    // PATTERN 7: PERFECT DICTIONARY ONLY (10 points)
    // PATTERN 8: NO RAW DATA ACCESS (10 points)
    // ========================================================================

    enrichStateWithPerfectProductDictionary(state) {
      try {
        if (!state || typeof state !== 'object') {
          return state;
        }

        // PATTERN 9: Get PPD from conductor delegation
        const ppd =
          window.MurphyDoorConfigurator.main?.getPerfectProductDictionary?.();
        if (!ppd) {
          isDev() &&
            console.warn(
              '[PersistenceManager] Perfect product dictionary not available for state enrichment'
            );
          return state;
        }

        const enrichedState = {};

        Object.entries(state).forEach(([categoryId, selection]) => {
          const enrichedSelection = { ...selection };

          // PATTERN 7: Get category type from PPD (O(1) Map lookup)
          const categoryData = ppd.categories.get(categoryId);
          if (categoryData) {
            enrichedSelection.categoryType = categoryData.type;
          }

          // PATTERN 7: Resolve variantId to productId via PPD's pre-built O(1) map
          if (selection.variantId && !selection.productId) {
            const productId = ppd.variantIdToProductIdMap?.get(
              selection.variantId
            );
            if (productId) {
              enrichedSelection.productId = productId;
            }
          }

          // Determine selection path if not present
          if (!enrichedSelection.selectionPath) {
            if (enrichedSelection.productId && !enrichedSelection.variantId) {
              enrichedSelection.selectionPath = 'product';
            } else if (enrichedSelection.variantId) {
              enrichedSelection.selectionPath = 'variant';
            }
          }

          enrichedState[categoryId] = enrichedSelection;
        });

        isDev() &&
          console.log(
            '[PersistenceManager] State enriched with perfect product dictionary data',
            {
              originalEntries: Object.keys(state).length,
              enrichedEntries: Object.keys(enrichedState).length,
            }
          );

        return enrichedState;
      } catch (error) {
        isDev() &&
          console.warn(
            '[PersistenceManager] Failed to enrich state with perfect product dictionary:',
            error
          );
        return state; // Return original state if enrichment fails
      }
    },

    // ========================================================================
    // URL RECOVERY DETECTION
    // ========================================================================

    hasUrlRecoveryData() {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const urlState = urlParams.get('config');
        return !!urlState;
      } catch (error) {
        isDev() &&
          console.warn(
            '[PersistenceManager] Error checking URL recovery data:',
            error
          );
        return false;
      }
    },

    /**
     * Programmatically checks if ANY stored state exists that clearAllStorage() would remove.
     * This ensures the detection logic stays synchronized with the clearing logic.
     *
     * @returns {boolean} True if any configurator state exists in storage
     * @private
     */
    _hasAnyStoredState() {
      try {
        // Check localStorage for STATE_MANAGER key
        if (localStorage.getItem(STORAGE_KEYS.STATE_MANAGER)) {
          return true;
        }

        // Check sessionStorage for session, configuration, or recovery keys
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (
            key &&
            (key.startsWith(STORAGE_KEYS.SESSION) ||
              key.startsWith(STORAGE_KEYS.CONFIGURATION) ||
              key === STORAGE_KEYS.RECOVERY)
          ) {
            return true;
          }
        }
      } catch (error) {
        isDev() &&
          console.warn(
            '[PersistenceManager] Error checking for stored state:',
            error
          );
      }

      return false;
    },

    // ========================================================================
    // STATE RECOVERY - 5-STAGE EVENT LIFECYCLE
    // PATTERN 3: 5-STAGE EVENT LIFECYCLE (10 points)
    // PATTERN 12: PERFORMANCE TRACKING (10 points)
    // PATTERN 14: TRY/CATCH + FAILED EVENTS (10 points)
    // ========================================================================

    async recoverState() {
      const startTime = performance.now();

      try {
        isDev() &&
          console.log(
            '[PersistenceManager] Starting state recovery with perfectProductDictionary available'
          );

        // STAGE 1: START
        await publishEvent(EVENTS.PERSISTENCE_RECOVERY_STARTED, {
          timestamp: Date.now(),
          hasUrlParams: !!new URLSearchParams(window.location.search).get(
            'config'
          ),
        });

        // First try URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const urlState = urlParams.get('config');

        // STAGE 2: VALIDATION
        await publishEvent(EVENTS.PERSISTENCE_RECOVERY_VALIDATION, {
          hasUrlState: !!urlState,
          hasStoredState: this._hasAnyStoredState(),
          timestamp: Date.now(),
        });

        if (urlState) {
          // STAGE 3: PROGRESS
          await publishEvent(EVENTS.PERSISTENCE_RECOVERY_PROGRESS, {
            stage: 'url_deserialization',
            source: 'url',
          });

          const state = this.deserializeState(urlState);
          if (state) {
            // Enrich state with PPD data (Pattern 7: PPD-first)
            const enrichedState =
              this.enrichStateWithPerfectProductDictionary(state);
            isDev() &&
              console.log(
                '[PersistenceManager] URL state recovered and enriched successfully'
              );

            const duration = performance.now() - startTime;
            trackPerformance('STATE_RECOVERY', duration);

            // STAGE 4: SUCCESS
            await publishEvent(EVENTS.PERSISTENCE_RECOVERY_SUCCESS, {
              source: 'url',
              duration,
              entryCount: Object.keys(enrichedState).length,
              timestamp: Date.now(),
            });

            return { source: 'url', state: enrichedState };
          }
        }

        // =====================================================================
        // GHOST SELECTION FIX: Smart localStorage clearing on clean URLs
        // GOLDEN RULE: Clean URL = Fresh Start | URL with ?config= = Intent to Restore
        // =====================================================================
        if (!urlState) {
          const hadStoredState = this._hasAnyStoredState();

          if (hadStoredState) {
            isDev() &&
              console.log(
                '[PersistenceManager] Clean URL detected - clearing previous session state to prevent ghost selections'
              );

            // Use the existing comprehensive method to ensure a true fresh start
            this.clearAllStorage();

            // Publish state cleared event for observability
            publishEvent(EVENTS.SYSTEM_STATE_CLEARED, {
              reason: 'clean_url_fresh_start',
              clearedSources: ['localStorage', 'sessionStorage'],
              timestamp: Date.now(),
              source: 'PersistenceManager.recoverState',
            });

            isDev() &&
              console.log(
                '[PersistenceManager] Ghost selection prevention complete - starting fresh'
              );
          } else {
            isDev() &&
              console.log(
                '[PersistenceManager] Clean URL with no stored state - starting fresh'
              );
          }

          const duration = performance.now() - startTime;
          trackPerformance('STATE_RECOVERY', duration);

          // STAGE 4: SUCCESS (no state to recover is still a successful operation)
          await publishEvent(EVENTS.PERSISTENCE_RECOVERY_SUCCESS, {
            source: 'none',
            reason: hadStoredState ? 'clean_url_cleared' : 'clean_url_no_state',
            duration,
            timestamp: Date.now(),
          });

          return null;
        }

        // Then try recovery point
        // STAGE 3: PROGRESS
        await publishEvent(EVENTS.PERSISTENCE_RECOVERY_PROGRESS, {
          stage: 'recovery_point',
          source: 'recovery',
        });

        const recovery = this.loadRecoveryPoint();
        if (recovery && recovery.state) {
          const enrichedState = this.enrichStateWithPerfectProductDictionary(
            recovery.state
          );
          isDev() &&
            console.log(
              '[PersistenceManager] Recovery point state recovered and enriched successfully'
            );

          const duration = performance.now() - startTime;
          trackPerformance('STATE_RECOVERY', duration);

          // STAGE 4: SUCCESS
          await publishEvent(EVENTS.PERSISTENCE_RECOVERY_SUCCESS, {
            source: 'recovery',
            duration,
            entryCount: Object.keys(enrichedState).length,
            timestamp: Date.now(),
          });

          return { source: 'recovery', state: enrichedState };
        }

        // Finally try session storage
        // STAGE 3: PROGRESS
        await publishEvent(EVENTS.PERSISTENCE_RECOVERY_PROGRESS, {
          stage: 'session_storage',
          source: 'session',
        });

        const session = this.loadFromSession('state');
        if (session) {
          const enrichedState =
            this.enrichStateWithPerfectProductDictionary(session);
          isDev() &&
            console.log(
              '[PersistenceManager] Session state recovered and enriched successfully'
            );

          const duration = performance.now() - startTime;
          trackPerformance('STATE_RECOVERY', duration);

          // STAGE 4: SUCCESS
          await publishEvent(EVENTS.PERSISTENCE_RECOVERY_SUCCESS, {
            source: 'session',
            duration,
            entryCount: Object.keys(enrichedState).length,
            timestamp: Date.now(),
          });

          return { source: 'session', state: enrichedState };
        }

        isDev() &&
          console.log(
            '[PersistenceManager] No state to recover - starting fresh'
          );

        const duration = performance.now() - startTime;
        trackPerformance('STATE_RECOVERY', duration);

        // STAGE 4: SUCCESS (no state found is still a valid completion)
        await publishEvent(EVENTS.PERSISTENCE_RECOVERY_SUCCESS, {
          source: 'none',
          reason: 'no_state_found',
          duration,
          timestamp: Date.now(),
        });

        return null;
      } catch (error) {
        isDev() &&
          console.error('[PersistenceManager] State recovery failed:', error);

        const duration = performance.now() - startTime;

        // STAGE 5: FAILED
        await publishEvent(EVENTS.PERSISTENCE_RECOVERY_FAILED, {
          error: error.message,
          stack: error.stack,
          duration,
          timestamp: Date.now(),
        });

        // Also publish the legacy error event for backward compatibility
        publishEvent(EVENTS.ERROR_PERSISTENCE_STATE_RECOVERY_FAILED, {
          operation: 'state_recovery',
          error: error.message,
          stack: error.stack,
          source: 'PersistenceManager',
          timestamp: Date.now(),
        });

        return null;
      }
    },

    // ========================================================================
    // PATTERN 15: CLEANUP - Unsubscribe all event listeners (10 points)
    // ========================================================================
    cleanup() {
      isDev() &&
        console.log(
          `[PersistenceManager] Cleaning up ${cleanupHandlers.length} subscriptions`
        );
      cleanupHandlers.forEach((unsubscribe) => {
        try {
          if (typeof unsubscribe === 'function') unsubscribe();
        } catch (error) {
          isDev() &&
            console.error('[PersistenceManager] Cleanup handler error:', error);
        }
      });
      cleanupHandlers.length = 0;
      isInitialized = false;

      publishEvent(EVENTS.MODULE_PERSISTENCE_MANAGER_CLEANUP, {
        module: 'persistenceManager',
        timestamp: Date.now(),
      });
    },
  };
})(window);
